import type {
  AdminOverview,
  AdminToken,
  AdminUser,
  AdminUserPatch,
  BackupList,
  BackupResult,
  CacheStats,
  EvictResult,
  JobStatus,
  TransferRequest,
  TransferResult,
} from '@studio/shared';
// Phase 7, Lane S: similar exercises
import type { DifficultyIconSetInfo, SimilarResponse } from '@studio/shared';
// Phase 8: the 2026 Typst archive
import type { ArchiveExerciseInfo, ArchiveMatchReport, ArchiveSheetInfo, ArchiveStatus } from '@studio/shared';
// Phase 8, Lane S: past sheets' Typst versions
import type { ArchiveSheetSummary, LibraryItem as ArchiveLibraryItem } from '@studio/shared';
import type {
  AnnotationKind,
  AnnotationSeverity,
  AnnotationStatus,
  AuthMethods,
  AuthoredExercise,
  ExerciseWork,
  ExerciseWorkStatus,
  BinEntry,
  ExerciseUsageSheet,
  ExerciseAnnotation,
  ExerciseAsset,
  ExerciseDetail,
  GitlabConfigView,
  GitlabHealth,
  GitlabLibraryInfo,
  GitlabSheetRow,
  EmbedFormat,
  HeaderPack,
  ImportLibraryResult,
  LatexHeaderChoice,
  ImportListing,
  ImportSheetResult,
  KeptChange,
  ResolveResponse,
  SyncResolution,
  LibraryResponse,
  PrepareStatus,
  PublishCheck,
  PublishPlan,
  PublishResult,
  PublishTarget,
  SheetGitlabStatus,
  Markup,
  RenderResult,
  RenderVariant,
  RevisionSummary,
  SheetDoc,
  SheetItem,
  SheetKind,
  SheetStatus,
  SheetSummary,
  SuggestionsResponse,
  TaxonomyEntry,
  TermCalendar,
  TermListEntry,
  TermUsage,
  TermView,
  User,
} from '@studio/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** the error's JSON body, for routes that say more than `error` (e.g. a publish's `code`) */
    readonly body?: any,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  /*
   * Parsed defensively, and only after `ok` is known. An error response is not always
   * JSON — Express's default handler and any reverse proxy in front of it send HTML — so
   * `JSON.parse` threw a SyntaxError that reached the caller instead of an ApiError, and
   * every `catch (e) { if (e instanceof ApiError) … }` missed it.
   */
  let data: any;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!res.ok) {
    /*
     * A 401 means the session went away — it expires after 30 days, and the tab may have
     * been open across it. Without this every page just sat on its spinner forever,
     * because each one only knows how to show "loading" or "loaded".
     */
    if (res.status === 401) window.dispatchEvent(new CustomEvent('studio:unauthenticated'));
    throw new ApiError(data?.error ?? text.slice(0, 200) ?? res.statusText, res.status, data);
  }
  return data as T;
}

/**
 * A header pack name, safe to put in a path.
 *
 * Names look like `2023/examHeader#examtitle`. The `#` starts a URL fragment, so an
 * un-encoded name truncated the request: `/api/headers/2023/examHeader#examtitle/preview`
 * reached the server as `/api/headers/2023/examHeader`, and the test-compile button, the
 * save and the delete all silently hit the wrong route. The `/` is kept literal because
 * the routes are `/:name(*)`, which is what lets a name carry one.
 */
const headerPath = (name: string) => name.split('/').map(encodeURIComponent).join('/');

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

const put = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) });

const patch = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) });

export interface LibraryFilters {
  q?: string;
  /**
   * `authored` lists the exercises written in this app instead of the corpus; `gitlab`
   * those imported from the 2026 course repository; `archive` the 2026 Typst archive's
   * exercises no 2025 exercise matches ("Pool 2025 (Typst, unused)", Phase 8)
   */
  source?: 'corpus' | 'authored' | 'gitlab' | 'archive';
  years?: string[];
  sheets?: string[];
  topics?: string[];
  types?: string[];
  goals?: string[];
  motifs?: string[];
  difficultyRated?: string[];
  difficultySource?: string[];
  difficultyMin?: number;
  difficultyMax?: number;
  difficultySourceMin?: number;
  difficultySourceMax?: number;
  timeMin?: number;
  timeMax?: number;
  categorisedOnly?: boolean;
  /** '0' shows superseded versions too; the default collapses them */
  canonicalOnly?: string;
  defectKind?: string[];
  defectSeverity?: string[];
  /** only exercises with a sample solution */
  hasSolution?: boolean;
  /** only exercises with a Typst version (the 2026 archive's port, or written in Typst) */
  hasTypst?: boolean;
  /** leave out exercises with an open, serious reported problem */
  noSeriousProblems?: boolean;
  codeLanguages?: string[];
  proseLanguages?: string[];
  sheetKinds?: string[];
  /** leave out what a Studio sheet of this term (`2026`) already uses, any version of it */
  notUsedTerm?: string;
  /**
   * `meaning` ranks the query by the similarity index (both languages, related words, code
   * names, topics); the default matches the words typed (Phase 7, Lane S)
   */
  mode?: 'meaning';
  /** the term the items' `usedThisTerm` counts; default the filter's, else the current one */
  usageTerm?: string;
  sort?: string;
  limit?: number;
  offset?: number;
}

/**
 * An exercise id as a URL path: each segment encoded. A course repository or archive id is
 * a path with `#<n>` (`git/…`, `a26/…`), which a URL would otherwise cut off as its
 * fragment; for the corpus's and authored ids this changes nothing.
 */
export function pathId(id: string): string {
  return id.split('/').map(encodeURIComponent).join('/');
}

/** The library page of an exercise. */
/** An exercise on the Exercises page (`/library/…` still leads there). */
export const libraryPath = (id: string): string => `/exercises/${pathId(id)}`;

export function libraryQueryString(f: LibraryFilters): string {
  const p = new URLSearchParams();
  const add = (key: string, value: unknown) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      if (value.length) p.set(key, value.join(','));
    } else if (typeof value === 'boolean') {
      if (value) p.set(key, '1');
    } else if (String(value).length) {
      p.set(key, String(value));
    }
  };
  for (const [key, value] of Object.entries(f)) add(key, value);
  return p.toString();
}

export interface CorpusSheetSummary {
  id: string;
  year: string;
  label: string;
  title: string;
  kind: string;
  layout: string;
  pdf: string | null;
  solPdf: string | null;
  pages: number;
  exerciseCount: number;
  mainFile: string;
  /** its Typst version in the 2026 archive, which a course sheet started from it takes (Phase 8) */
  typst?: ArchiveSheetSummary | null;
  /** a sheet only the 2026 archive has (no PDF; a copy is a course sheet) */
  archiveOnly?: boolean;
}

export interface Stats {
  exercises: number;
  categorised: number;
  sheets: number;
  years: number;
  perYear: { year: string; exercises: number; sheets: number }[];
  topTopics: { value: string; label: string; count: number }[];
  timeBuckets: { label: string; count: number }[];
  families: number;
  superseded: number;
  variants: number;
  defects: number;
  defectsHigh: number;
}

export interface HeaderListing {
  headers: HeaderPack[];
  /** the raw packs on disk, so an author can see which commands exist */
  packs: { name: string; year: string; sheetType: string | null; heads: { name: string; arity: number; optional: boolean }[] }[];
}

export interface AnnotationList {
  total: number;
  items: ExerciseAnnotation[];
  facets: {
    kind: Record<AnnotationKind, number>;
    severity: Record<AnnotationSeverity, number>;
    status: Record<AnnotationStatus, number>;
  };
  /** Studio sheets of `term` using each exercise on this page (absent: none) */
  usedOnSheets?: Record<string, number>;
  term?: string;
}

export interface DuplicateWarning {
  /**
   * `same-term`: another Studio sheet of the term already has it (Phase 6); `similar-term`:
   * it is very like an exercise another sheet of the term has (Phase 7, Lane S)
   */
  kind: 'same-exercise' | 'same-family' | 'same-year' | 'same-term' | 'similar-term';
  exerciseId: string;
  otherId: string;
  otherYear?: string | null;
  otherTitle?: string | null;
  otherPrinted?: string | null;
  relation?: string | null;
  message: string;
  /** `same-term`: the sheet that has it, and its week */
  otherSheetId?: string;
  otherSheetTitle?: string;
  otherWeek?: number | null;
}

export interface ApiTokenView {
  id: number;
  name: string;
  prefix: string;
  scopes: string[];
  lastUsedAt?: string | null;
  createdAt: string;
  token?: string;
}

/** An app-written exercise seen through the corpus lens the UI already speaks. */
function authoredAsDetail(a: AuthoredExercise): ExerciseDetail {
  // composed on the server: a Typst solution needs its `#answer[…]` wrapper added
  const code = a.body ?? [a.code, a.answerCode].filter(Boolean).join('\n\n');
  return {
    id: a.id,
    // carried through, or the editor cannot tell that this exercise is Typst and
    // previews it with pdflatex
    markup: a.markup ?? 'latex',
    year: 'mine',
    sheetId: '',
    sheetLabel: '',
    sheetTitle: '',
    index: 0,
    title: a.title,
    printedNumber: null,
    summary: a.summary,
    topics: a.topics ?? [],
    exerciseTypes: a.exerciseTypes ?? [],
    learningGoals: a.learningGoals ?? [],
    motifs: a.motifs ?? [],
    difficultyRated: a.difficultyRated ?? 0,
    heading: a.heading ?? null,
    latexHeader: a.headerName,
    difficultySource: a.difficulty ?? null,
    timeMinutes: a.timeMinutes ?? 0,
    points: a.points ?? null,
    pages: a.pages,
    // the file is rewritten under the same name by every compile: the version keeps a cached picture from showing
    thumb: a.thumb ? `${a.thumb}?v=${encodeURIComponent(a.updatedAt)}` : null,
    // a Typst exercise usually has its solution inline, as `#solution[…]` in the body
    hasSolution:
      Boolean(a.answerCode?.trim()) ||
      ((a.markup ?? 'latex') === 'typst' ? /#(?:solution|answer)\s*\[/.test(a.code) : /\\begin\{answer\}/.test(a.code)),
    signals: {
      chars: code.length,
      lines: code.split('\n').length,
      codeBlocks: 0,
      hasTikz: false,
      hasProofTree: false,
      hasTable: false,
      hasImage: Boolean(a.assets?.length),
      hasEnumerate: false,
      subtasks: 0,
      language: 'none',
      german: a.language === 'de',
    },
    code,
    exerciseCode: a.code,
    answerCode: a.answerCode ?? null,
    wrapper: '',
    sourcePath: null,
    pdf: a.pdfCropped ?? a.pdf ?? null,
    solPdf: a.solPdf ?? null,
    solPages: a.solPages,
    competencies: a.competencies ?? [],
    text: '',
    siblings: [],
    editedFrom: a.supersedes ?? null,
  };
}

/** The details of an exercise one can change without touching its text (`PUT …/details`). */
export interface ExerciseDetailsPatch {
  /** its name, as the Exercises page lists it */
  title?: string;
  /** what sheets print as its heading; '' or null: the name */
  heading?: string | null;
  /** its LaTeX header (a registry name); '' or null: automatic (a written exercise: the default) */
  latexHeader?: string | null;
  points?: number | null;
  /** camels, as a sheet prints them */
  difficultySource?: number | null;
  /** the 1–5 rating */
  difficultyRated?: number | null;
  timeMinutes?: number | null;
  summary?: string;
  competencies?: string[];
  proseLanguage?: 'de' | 'en';
  codeLanguage?: 'sml' | 'ocaml' | 'none';
  topics?: string[];
  exerciseTypes?: string[];
  learningGoals?: string[];
  motifs?: string[];
}

/** One exercise in an "Assigned to me" / "Created by me" list. */
export interface ExerciseWorkItem {
  id: string;
  title: string;
  source: 'corpus' | 'archive' | 'gitlab' | 'studio';
  supersedes?: string | null;
  work: ExerciseWork;
}

/** A version of an exercise written in Studio, from its history. */
export interface AuthoredRevision {
  id: number;
  userId: number | null;
  userName: string | null;
  kind: 'create' | 'save' | 'before-restore' | 'restore' | 'file' | string;
  createdAt: string;
  exercise: AuthoredExercise;
  /** a change to one of its files (`kind: 'file'`): the text after it, when kept */
  file?:
    | { filename: string; action: 'edit' | 'upload'; text?: string }
    | { filename: string; action: 'delete' }
    | { filename: string; action: 'rename'; to: string };
}

/** A text file of an exercise, as its editor's file tab holds it. */
export interface ExerciseTextFile {
  filename: string;
  text: string;
  version: string;
}

/** What drawing a LaTeX figure source into its PDF came to (server figureFiles.ts). */
export type FigureBuild =
  | { ok: true; pdf: string; pages: number; warning?: string }
  | { ok: false; pdf: string; message: string; errors: string[] };

export interface FileWrite {
  file: ExerciseTextFile;
  assets: ExerciseAsset[];
  figure?: FigureBuild;
}

/** The files an exercise reads, for its view in the library. */
export interface ExerciseFileList {
  editable: boolean;
  files: { filename: string; mime: string; bytes: number; url: string }[];
}

export interface Person {
  id: number;
  name: string;
  color: string | null;
}

/** `GET /api/course/status`: whether this server can build course sheets, and from what */
export interface CourseStatus {
  /** a snapshot of the 2026 course headers is installed */
  available: boolean;
  hash: string | null;
  /** the course repository's commit the snapshot was taken from */
  commit: string | null;
  dirty: boolean;
  createdAt: string | null;
  packages: { name: string; cached: boolean }[];
  typstVersion: string | null;
  ciTypstVersion: string;
}

/** what the sheet list carries beyond the shared summary */
export type SheetSummaryWithTerm = SheetSummary & { termYear?: string | null };

/**
 * Filters of `GET /api/sheets` (Phase 6). `term`/`week` take `'none'` for "not set";
 * `bin: true` lists the bin instead (your binned sheets; an admin's: everyone's).
 */
export interface SheetListQuery {
  term?: string | 'none';
  week?: number | 'none';
  status?: SheetStatus | SheetStatus[];
  kind?: SheetKind | SheetKind[];
  bin?: boolean;
}

export function sheetListQueryString(q: SheetListQuery = {}): string {
  const p = new URLSearchParams();
  if (q.term) p.set('term', q.term);
  if (q.week !== undefined) p.set('week', String(q.week));
  const many = (v: string | string[] | undefined) => (Array.isArray(v) ? v.join(',') : v ?? '');
  if (many(q.status)) p.set('status', many(q.status));
  if (many(q.kind)) p.set('kind', many(q.kind));
  if (q.bin) p.set('bin', '1');
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * `POST /api/gitlab/snapshot/refresh`: the course template taken from GitLab. Read
 * leniently — only what the Integrations page shows.
 */
export interface GitlabSnapshotResult {
  /** `unchanged` when the template in GitLab is the one already in use */
  status?: 'updated' | 'unchanged';
  /** the commit it was taken from */
  commit?: string | null;
}

/** `PATCH /api/sheets/:id/meta` (Phase 6): `null` clears the field */
export interface SheetMetaPatch {
  termYear?: string | null;
  week?: number | null;
  status?: SheetStatus | null;
}

/** `GET /api/home/attention`: a reported problem on an exercise of your sheets */
export interface AttentionProblem {
  annotationId: number;
  exerciseId: string;
  exerciseTitle: string | null;
  kind: string;
  severity: string;
  title: string;
  /** SQLite UTC datetime */
  createdAt: string;
  sheets: { id: string; title: string; label: string; kind: string; week: number | null }[];
}

/** `GET /api/home/week`: the same week in past years */
export interface WeekInPastYears {
  week: number;
  kind: string;
  /** the sheet number of that week (`0`, `A`, …), or null past the letters */
  label: string | null;
  pastSheets: {
    id: string;
    year: string;
    label: string;
    title: string;
    kind: string;
    exerciseCount: number;
    /** its Typst version in the 2026 archive (Phase 8) */
    typst?: ArchiveSheetSummary | null;
  }[];
  topics: { value: string; label: string; count: number }[];
}

export const api = {
  me: () =>
    request<{ user: User | null; bootstrap: boolean; auth: AuthMethods }>('/api/auth/me'),
  /** name-only sign-in; only reachable while the server has debug login enabled */
  debugLogin: (name: string) => post<{ user: User }>('/api/auth/debug-login', { name }),
  logout: () => post<void>('/api/auth/logout'),
  users: () => request<{ id: number; name: string; email: string; color: string }[]>('/api/auth/users'),

  taxonomy: () =>
    request<{ topics: TaxonomyEntry[]; types: TaxonomyEntry[]; goals: TaxonomyEntry[] }>(
      '/api/taxonomy',
    ),
  stats: () => request<Stats>('/api/library/stats'),

  exercises: (f: LibraryFilters) =>
    request<LibraryResponse>(`/api/library/exercises?${libraryQueryString(f)}`),
  exercise: (id: string) => request<ExerciseDetail>(`/api/library/exercises/${pathId(id)}`),
  /** exercises like this one, best first, its own versions apart (Phase 7, Lane S) */
  similarExercises: (id: string, opts: { limit?: number; usageTerm?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.limit) p.set('limit', String(opts.limit));
    if (opts.usageTerm) p.set('usageTerm', opts.usageTerm);
    const q = p.toString();
    // a course repository id carries `#<n>`, which a URL would cut off
    return request<SimilarResponse>(`/api/library/exercises/${id.split('/').map(encodeURIComponent).join('/')}/similar${q ? `?${q}` : ''}`);
  },
  corpusSheets: () => request<CorpusSheetSummary[]>('/api/library/corpus-sheets'),
  corpusSheet: (id: string) =>
    request<{
      sheet: Record<string, unknown>;
      exercises: ExerciseDetail[];
      /** its Typst version in the 2026 archive (Phase 8), and the exercises only that version has */
      typst?: ArchiveSheetSummary | null;
      typstExtra?: ArchiveLibraryItem[];
    }>(`/api/library/corpus-sheets/${id}`),

  sheets: (query?: SheetListQuery) => request<SheetSummaryWithTerm[]>(`/api/sheets${sheetListQueryString(query)}`),
  /**
   * One request: items may be bare `{ exerciseId }`, and the server fills in each
   * exercise's text. Creating first and then writing the items back was two round trips
   * and lost the bodies whenever the second one went wrong.
   */
  createSheet: (body: {
    title: string;
    meta?: Partial<SheetDoc['meta']>;
    items?: { kind?: string; exerciseId?: string; includeSolution?: boolean; body?: string }[];
    /** on a course sheet, place a 2025 exercise as its native Typst version where the 2026 archive has one */
    typst?: boolean;
  }) => post<SheetSummaryWithTerm>('/api/sheets', body),
  /**
   * Copy a past sheet; `meta` overrides what the copy takes from the original. A course
   * sheet copied from a sheet with a Typst version takes that version's exercises
   * (Phase 8) unless `typst` is false.
   */
  copyCorpusSheet: (corpusId: string, title?: string, meta?: Partial<SheetDoc['meta']>, opts: { typst?: boolean } = {}) =>
    post<SheetSummaryWithTerm>(`/api/sheets/from-corpus/${corpusId}`, {
      title,
      meta,
      ...(opts.typst === false ? { typst: false } : {}),
    }),
  sheet: (id: string) =>
    request<{
      summary: SheetSummary;
      doc: SheetDoc;
      participants: { userId: number; name: string; color: string }[];
      /** its term is over: its items keep their text */
      frozen?: boolean;
    }>(`/api/sheets/${id}`),
  saveSheet: (id: string, doc: SheetDoc) => request<SheetSummary>(`/api/sheets/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ doc }),
    headers: { 'Content-Type': 'application/json' },
  }),
  /** moves the sheet to the bin (owner or admin); `restoreSheet` undoes it for 30 days */
  deleteSheet: (id: string) => request<void>(`/api/sheets/${id}`, { method: 'DELETE' }),
  duplicateSheet: (id: string) => post<SheetSummary>(`/api/sheets/${id}/duplicate`),
  /** Append items without replacing the document — safe while others are editing. */
  appendItems: (id: string, items: { kind: string; exerciseId?: string; includeSolution?: boolean }[], opts: { typst?: boolean } = {}) =>
    post<SheetSummary>(`/api/sheets/${id}/items`, { items, ...opts }),
  invite: (id: string, email: string, role: 'editor' | 'viewer') =>
    post<SheetSummary>(`/api/sheets/${id}/collaborators`, { email, role }),
  removeCollaborator: (id: string, userId: number) =>
    request<SheetSummary>(`/api/sheets/${id}/collaborators/${userId}`, { method: 'DELETE' }),
  setCollaboratorRole: (id: string, userId: number, role: 'editor' | 'viewer') =>
    patch<SheetSummary>(`/api/sheets/${id}/collaborators/${userId}`, { role }),
  duplicateCheck: (id: string, exerciseIds: string[], year?: string) =>
    post<{ warnings: DuplicateWarning[] }>(`/api/sheets/${id}/duplicate-check`, {
      exerciseIds,
      year,
    }),
  /**
   * Save a version of the sheet. `before-bulk` marks the state a bulk change is about to
   * replace (History shows it as "Before bulk change"); everything else is `named`.
   */
  createRevision: (id: string, note: string, kind: 'named' | 'before-bulk' = 'named') =>
    post<{ ok: true; id: number }>(`/api/sheets/${id}/revisions`, { note, kind }),
  revisions: (id: string) => request<RevisionSummary[]>(`/api/sheets/${id}/revisions`),
  /** One version with its whole document, to compare with the sheet as it is now. */
  revision: (id: string, revisionId: number) =>
    request<{ revision: RevisionSummary; doc: SheetDoc }>(`/api/sheets/${id}/revisions/${revisionId}`),
  /** Put one item back as a version had it; the rest of the sheet is left alone. */
  restoreRevisionItem: (id: string, revisionId: number, itemId: string) =>
    post<{ ok: true; outcome: 'replaced' | 'inserted' }>(
      `/api/sheets/${id}/revisions/${revisionId}/restore-item`,
      { itemId },
    ),
  /**
   * Change an exercise's details — everything but its text: tags, title, points, camels,
   * rating, time, summary, competencies, languages. A corpus or archive exercise is fixed
   * in place for everyone; one of the course repository (`git/…`) cannot be changed here.
   */
  updateDetails: (id: string, patch: ExerciseDetailsPatch) =>
    put<void>(`/api/library/exercises/${pathId(id)}/details`, patch),
  /** @deprecated the same as `updateDetails` */
  updateTags: (id: string, patch: ExerciseDetailsPatch) =>
    put<void>(`/api/library/exercises/${pathId(id)}/details`, patch),
  restoreRevision: (id: string, revisionId: number) =>
    post<void>(`/api/sheets/${id}/revisions/${revisionId}/restore`),

  /**
   * Build one version of a sheet. `solution` is sent alongside `variant` so that a server
   * which predates variants still builds the right one of the two it knows; a newer one
   * reads `variant` first. A course sheet ignores `format`: it only has the one build.
   */
  renderSheet: (
    id: string,
    opts: { variant?: RenderVariant; solution?: boolean; format?: 'latex' | 'typst'; force?: boolean },
  ) => {
    const variant = opts.variant ?? (opts.solution ? 'solution' : 'student');
    return post<RenderResult>(`/api/render/sheet/${id}`, {
      variant,
      solution: variant !== 'student',
      format: opts.format,
      force: opts.force,
    });
  },
  preview: (body: {
    body: string;
    exerciseId?: string | null;
    solution?: boolean;
    label?: string;
    counter?: number;
    /** which compiler to use; without it the server assumes LaTeX */
    markup?: Markup;
    /** the item is on a course sheet: an archive exercise is compiled as the sheet builds it (Phase 8) */
    course?: boolean;
    /**
     * The sheet the item is on (items without their bodies) and the item: the preview is
     * then compiled the way the sheet build compiles it, against the same LaTeX header.
     */
    sheet?: { meta: SheetDoc['meta']; items: (Omit<SheetItem, 'body'> & { body?: string })[] };
    itemId?: string;
  }) => post<RenderResult & { header?: LatexHeaderChoice }>('/api/render/preview', body),
  /**
   * Which LaTeX header each LaTeX item of a sheet compiles against, keyed by item id
   * (`#footNote` for the foot note) — decided by the same function the builds use.
   */
  latexHeaders: (doc: { meta: SheetDoc['meta']; items: (Omit<SheetItem, 'body'> & { body?: string })[] }) =>
    post<{ format: EmbedFormat; headers: Record<string, LatexHeaderChoice> }>('/api/render/latex-headers', { doc }),
  sheetSource: (id: string, format: 'latex' | 'typst' | 'bundle', solution: boolean) =>
    fetch(`/api/render/sheet/${id}/source?format=${format}&solution=${solution ? 1 : 0}`, {
      credentials: 'same-origin',
    }).then((r) => r.text()),
  /**
   * One generated file of a course sheet: the main `.typ` by default, or `file`, a path
   * relative to the sheet's directory (`exercises/<slug>.typ`). The same files build
   * every version, so there is no solution switch. `main` is the main file's name, which
   * the server sends in a header because the body is the file itself.
   */
  courseSource: async (id: string, file?: string): Promise<{ text: string; main: string | null }> => {
    const res = await fetch(
      `/api/render/sheet/${id}/source?format=typst${file ? `&file=${encodeURIComponent(file)}` : ''}`,
      { credentials: 'same-origin' },
    );
    const text = await res.text();
    if (!res.ok) {
      let message = text.slice(0, 200) || res.statusText;
      try {
        message = JSON.parse(text).error ?? message;
      } catch {
        /* not JSON; the text is the message */
      }
      throw new ApiError(message, res.status);
    }
    return { text, main: res.headers.get('X-Studio-Course-Main') };
  },
  courseStatus: () => request<CourseStatus>('/api/course/status'),

  /* -------------------------------------------------------------- headers */

  headers: () => request<HeaderListing>('/api/headers'),
  /** one pack; a builtin's `tex` is its file, read off disk (the listing leaves it out) */
  header: (name: string) => request<HeaderPack & { texReadOnly?: boolean }>(`/api/headers/${headerPath(name)}`),
  createHeader: (body: {
    displayName: string;
    basedOn: string;
    tex: string;
    typst?: string;
    sheetType?: string;
    headCommand?: string;
    headKindLabel?: string;
    headArity?: number;
  }) => post<HeaderPack>('/api/headers', body),
  updateHeader: (name: string, body: Partial<HeaderPack> & { tex?: string; typst?: string | null }) =>
    put<HeaderPack>(`/api/headers/${headerPath(name)}`, body),
  deleteHeader: (name: string) =>
    request<void>(`/api/headers/${headerPath(name)}`, { method: 'DELETE' }),
  previewHeader: (name: string, body?: { label?: string; title?: string; force?: boolean }) =>
    post<RenderResult>(`/api/headers/${headerPath(name)}/preview`, body ?? {}),

  /* ------------------------------------------------------------ authoring */

  /**
   * One exercise from wherever it lives.  Sheets may mix corpus exercises with
   * ones written in the app, and the editor should not have to care which.
   */
  anyExercise: (id: string): Promise<ExerciseDetail> =>
    id.startsWith('user/')
      ? request<AuthoredExercise>(`/api/authoring/${id}`).then(authoredAsDetail)
      : // a course repository or archive exercise's id is a path with `#<n>`, which a URL would cut off
        request<ExerciseDetail>(`/api/library/exercises/${pathId(id)}`),

  authored: () => request<AuthoredExercise[]>('/api/authoring'),

  /* ------------------------------------------------- the 2026 Typst archive (Phase 8) */
  archiveStatus: () => request<ArchiveStatus>('/api/archive/status'),
  archiveReport: () => request<ArchiveMatchReport>('/api/archive/report'),
  archiveExercise: (id: string) => request<ArchiveExerciseInfo>(`/api/archive/exercises/${pathId(id)}`),
  archiveSheets: () => request<ArchiveSheetInfo[]>('/api/archive/sheets'),
  /** one block compiled against the course snapshot; `pdf` is a name under /api/render/pdf/ */
  renderArchive: (id: string, variant: RenderVariant = 'student', force = false) =>
    post<RenderResult>('/api/archive/render', { id, variant, force }),
  authoredExercise: (id: string) => request<AuthoredExercise>(`/api/authoring/${id}`),
  /** a new exercise made from this one, beside it (`variantOf`): another version for some sheets */
  makeVariant: (id: string) => post<AuthoredExercise>(`/api/exercises/${pathId(id)}/variant`, {}),
  /** a corpus or archive exercise's one shared edited version: the existing one, or a new copy */
  editVersion: (id: string) => post<AuthoredExercise>(`/api/exercises/${pathId(id)}/edit-version`, {}),
  authoredRevisions: (id: string) => request<AuthoredRevision[]>(`/api/authoring/${id}/revisions`),
  restoreAuthoredRevision: (id: string, revisionId: number) =>
    post<AuthoredExercise>(`/api/authoring/${id}/revisions/${revisionId}/restore`, {}),
  /* who works on an exercise (any source), and where that stands */
  exerciseWork: (id: string) => request<ExerciseWork>(`/api/exercises/${pathId(id)}/work`),
  setAssignees: (id: string, userIds: number[]) => put<ExerciseWork>(`/api/exercises/${pathId(id)}/assignees`, { userIds }),
  claimExercise: (id: string) => post<ExerciseWork>(`/api/exercises/${pathId(id)}/claim`, {}),
  unclaimExercise: (id: string) => request<ExerciseWork>(`/api/exercises/${pathId(id)}/claim`, { method: 'DELETE' }),
  setWorkStatus: (id: string, status: ExerciseWorkStatus) => put<ExerciseWork>(`/api/exercises/${pathId(id)}/status`, { status }),
  /** "I have it open"; answers who else has. `leaving` says goodbye. */
  exercisePresence: (id: string, leaving = false) => post<Person[]>(`/api/exercises/${pathId(id)}/presence`, leaving ? { leaving } : {}),
  exerciseWorkList: (q: { assigned?: 'me' | number; created?: 'me'; status?: ExerciseWorkStatus }) => {
    const p = new URLSearchParams();
    if (q.assigned !== undefined) p.set('assigned', String(q.assigned));
    if (q.created) p.set('created', q.created);
    if (q.status) p.set('status', q.status);
    return request<ExerciseWorkItem[]>(`/api/exercises/work?${p}`);
  },
  people: () => request<Person[]>('/api/exercises/people'),
  createAuthored: (body: Partial<AuthoredExercise> & { code: string }) =>
    post<AuthoredExercise>('/api/authoring', body),
  updateAuthored: (id: string, body: Partial<AuthoredExercise>) =>
    put<AuthoredExercise>(`/api/authoring/${id}`, body),
  deleteAuthored: (id: string) => request<void>(`/api/authoring/${id}`, { method: 'DELETE' }),
  /** `variant: 'marking'` is the marking scheme of a Typst exercise; it is never stored */
  renderAuthored: (id: string, opts: { solution?: boolean; force?: boolean; variant?: RenderVariant } = {}) =>
    post<RenderResult>(`/api/authoring/${id}/render`, opts),
  uploadExerciseFile: (file: File) =>
    request<AuthoredExercise>(`/api/authoring/upload?filename=${encodeURIComponent(file.name)}`, {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': 'application/octet-stream', 'x-filename': file.name },
    }),
  uploadAsset: (id: string, file: File) =>
    request<ExerciseAsset[]>(`/api/authoring/${id}/assets`, {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-filename': file.name,
      },
    }),
  deleteAsset: (id: string, filename: string) =>
    request<ExerciseAsset[]>(`/api/authoring/${id}/assets/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    }),
  /* an exercise's files as its editor's tabs use them */
  exerciseFile: (id: string, filename: string) =>
    request<ExerciseTextFile>(`/api/authoring/${id}/files/${encodeURIComponent(filename)}`),
  /** `base`: the version the editor read, null for a file it makes; a change made elsewhere is a 409 */
  writeExerciseFile: (id: string, filename: string, text: string, base?: string | null) =>
    put<FileWrite>(`/api/authoring/${id}/files/${encodeURIComponent(filename)}`, base === undefined ? { text } : { text, base }),
  renameExerciseFile: (id: string, filename: string, to: string) =>
    patch<AuthoredExercise>(`/api/authoring/${id}/files/${encodeURIComponent(filename)}`, { to }),
  newFigure: (id: string) => post<FileWrite>(`/api/authoring/${id}/files/new-figure`, {}),
  exerciseFiles: (id: string) => request<ExerciseFileList>(`/api/exercises/${pathId(id)}/files`),

  /* ---------------------------------------------- difficulty icons (sheet details) */
  iconSets: () => request<DifficultyIconSetInfo[]>('/api/icon-sets'),
  /** four pictures (PNG, JPEG or PDF), base64, one per level */
  uploadIconSet: (name: string, pictures: string[]) => post<DifficultyIconSetInfo>('/api/icon-sets', { name, pictures }),
  deleteIconSet: (id: string) =>
    request<void>(`/api/icon-sets/${encodeURIComponent(id.replace(/^custom:/, ''))}`, { method: 'DELETE' }),
  iconUrl: (set: string, level: number) => `/api/icon-sets/${encodeURIComponent(set)}/${level}`,

  /* ---------------------------------------------------------- annotations */

  annotations: (f: {
    exerciseId?: string;
    kind?: string;
    severity?: string;
    status?: string;
    source?: string;
    year?: string;
    q?: string;
    /** only problems on exercises a sheet of this term uses */
    usedTerm?: string;
    /** the term `usedOnSheets` counts */
    term?: string;
    limit?: number;
    offset?: number;
  } = {}) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') p.set(k, String(v));
    return request<AnnotationList>(`/api/annotations?${p.toString()}`);
  },
  fileAnnotation: (body: {
    exerciseId: string;
    kind: AnnotationKind;
    severity: AnnotationSeverity;
    title: string;
    detail?: string;
    suggestion?: string;
  }) => post<ExerciseAnnotation>('/api/annotations', body),
  updateAnnotation: (
    id: number,
    body: { status?: AnnotationStatus; title?: string; detail?: string; suggestion?: string },
  ) => patch<ExerciseAnnotation>(`/api/annotations/${id}`, body),
  deleteAnnotation: (id: number) => request<void>(`/api/annotations/${id}`, { method: 'DELETE' }),

  /* ------------------------------------------------------- gitlab (page) */

  gitlabConfig: () => request<GitlabConfigView>('/api/gitlab/config'),
  /** admins only; `fresh` asks GitLab again instead of the answer from the last 10 s */
  gitlabHealth: (fresh = false) => request<GitlabHealth>(`/api/gitlab/health${fresh ? '?fresh=1' : ''}`),
  gitlabSheets: () => request<GitlabSheetRow[]>('/api/gitlab/sheets'),
  /** admins: what "Prepare the repository" would change, and its waiting merge request */
  gitlabPrepareStatus: () => request<PrepareStatus>('/api/gitlab/prepare'),
  gitlabPrepare: () => post<PrepareStatus & { created: boolean }>('/api/gitlab/prepare'),
  gitlabRefreshTemplate: () => post<GitlabSnapshotResult>('/api/gitlab/snapshot/refresh'),

  /* ------------------------------------------------- gitlab (publishing a sheet) */

  /** `fresh` asks GitLab again instead of the answer from the last half minute */
  gitlabSheetStatus: (id: string, fresh = false) =>
    request<SheetGitlabStatus>(`/api/gitlab/sheets/${encodeURIComponent(id)}/status${fresh ? '?fresh=1' : ''}`),
  /** the dry run, with a diff per changed text file */
  gitlabPlan: (id: string, target: PublishTarget = 'mr') =>
    post<PublishPlan>(`/api/gitlab/sheets/${encodeURIComponent(id)}/plan`, { target }),
  gitlabCheck: (id: string) => post<PublishCheck>(`/api/gitlab/sheets/${encodeURIComponent(id)}/check`),
  /** `planHash` is the hash of the plan the tutor looked at; another plan now is a 409 */
  gitlabPublish: (id: string, target: PublishTarget, planHash: string) =>
    post<PublishResult>(`/api/gitlab/sheets/${encodeURIComponent(id)}/publish`, { target, planHash }),
  gitlabLink: (id: string, dir: string) => post<SheetGitlabStatus>(`/api/gitlab/sheets/${encodeURIComponent(id)}/link`, { dir }),
  /** automatic sync with the open review (409 `no-review` without one) */
  gitlabAutoSync: (id: string, on: boolean) =>
    request<SheetGitlabStatus>(`/api/gitlab/sheets/${encodeURIComponent(id)}/auto-sync`, { method: 'PUT', body: JSON.stringify({ on }) }),
  gitlabUnlink: (id: string) =>
    request<SheetGitlabStatus>(`/api/gitlab/sheets/${encodeURIComponent(id)}/link`, { method: 'DELETE' }),
  gitlabCiPdfUrl: (id: string, variant: RenderVariant) =>
    `/api/gitlab/sheets/${encodeURIComponent(id)}/ci-pdf?variant=${variant}`,

  /* ------------------------------------------------- gitlab (sync and import) */

  /**
   * A file changed in GitLab: take its version into the sheet (`remote`), keep the
   * sheet's (`keep`; the next publish replaces GitLab's), or merge both (`merge`).
   * `remoteCommit` is the change the card showed; a newer one is a 409 `changed-again`.
   */
  gitlabResolve: (id: string, body: { path: string; action: SyncResolution; remoteCommit?: string | null }) =>
    post<ResolveResponse>(`/api/gitlab/sheets/${encodeURIComponent(id)}/resolve`, body),
  /** GitLab changes the next publish will replace, and whose they are */
  gitlabKept: (id: string) => request<KeptChange[]>(`/api/gitlab/sheets/${encodeURIComponent(id)}/kept`),
  gitlabImportListing: (path = 'sheets') => request<ImportListing>(`/api/gitlab/repo/tree?path=${encodeURIComponent(path)}`),
  gitlabImportSheet: (path: string) => post<ImportSheetResult>('/api/gitlab/import-sheet', { path }),
  gitlabLibraryInfo: () => request<GitlabLibraryInfo>('/api/gitlab/library'),
  /** admins: every exercise block of the repository into the library */
  gitlabImportLibrary: () => post<ImportLibraryResult>('/api/gitlab/import-library'),

  /* ------------------------------------------------- week model (Phase 6) */

  /** your binned sheets; an admin gets everyone's */
  sheetBin: () => request<BinEntry[]>('/api/sheets/bin'),
  restoreSheet: (id: string) => post<SheetSummaryWithTerm>(`/api/sheets/${encodeURIComponent(id)}/restore`),
  /** admins: delete a binned sheet for good */
  purgeSheet: (id: string) => request<void>(`/api/sheets/${encodeURIComponent(id)}/purge`, { method: 'DELETE' }),

  terms: () => request<TermListEntry[]>('/api/terms'),
  /** 404 (ApiError) when the term has no calendar */
  term: (year: string) => request<TermView>(`/api/terms/${encodeURIComponent(year)}`),
  /** admins */
  saveTerm: (year: string, cal: Pick<TermCalendar, 'start' | 'weeks' | 'skipped' | 'holidays'>) =>
    put<TermView>(`/api/terms/${encodeURIComponent(year)}`, cal),
  /** admins: back to the course config; undefined when the config has no calendar for that year */
  resetTerm: (year: string) => request<TermView | undefined>(`/api/terms/${encodeURIComponent(year)}`, { method: 'DELETE' }),

  /** the sheets an exercise is on (binned ones never) */
  exerciseUsage: (id: string) =>
    request<{ exerciseId: string; sheets: ExerciseUsageSheet[] }>(
      `/api/usage/exercise/${id.split('/').map(encodeURIComponent).join('/')}`,
    ),
  /** the exercise ids that term's sheets use */
  termUsage: (year: string) => request<TermUsage>(`/api/usage/term/${encodeURIComponent(year)}`),

  /* ------------------------------------------- Home and Sheets views (Phase 6, Lane H) */

  /**
   * Set term, week or status without opening the sheet; `null` clears one. `published`
   * only for the owner or an admin (403 otherwise).
   */
  patchSheetMeta: (id: string, fields: SheetMetaPatch) =>
    patch<SheetSummaryWithTerm>(`/api/sheets/${encodeURIComponent(id)}/meta`, fields),
  /** new reported problems on exercises of your sheets of that term */
  homeAttention: (term: string) =>
    request<{ term: string; problems: AttentionProblem[] }>(`/api/home/attention?term=${encodeURIComponent(term)}`),
  /** the same week in past years: past sheets of that number and their usual topics */
  weekInPastYears: (week: number, kind: SheetKind = 'tutorial') =>
    request<WeekInPastYears>(`/api/home/week?week=${week}&kind=${encodeURIComponent(kind)}`),
  /**
   * Lane R's `GET /api/suggestions`, as the week drawer reads it (leniently: only
   * `exercises` is used). A server without the route answers 404; the drawer then shows none.
   */
  weekSuggestions: (q: { term: string; week: number; kind?: string; limit?: number; course?: boolean }) =>
    request<{ exercises?: unknown[] }>(
      `/api/suggestions?term=${encodeURIComponent(q.term)}&week=${q.week}&kind=${encodeURIComponent(q.kind ?? 'tutorial')}&limit=${q.limit ?? 8}${q.course ? '&course=1' : ''}`,
    ),

  /* ------------------------------------------ Review and suggestions (Phase 6, Lane R) */

  /**
   * The same week in past years and exercises for it that the term does not use yet,
   * ranked, with reasons. `term` defaults to the current one on the server.
   */
  suggestions: (q: { week: number; term?: string; kind?: string; limit?: number; course?: boolean }) => {
    const p = new URLSearchParams({ week: String(q.week) });
    if (q.term) p.set('term', q.term);
    if (q.kind) p.set('kind', q.kind);
    if (q.limit) p.set('limit', String(q.limit));
    // for a course (2026 Typst) sheet: Typst versions first, the archive's own exercises too (Phase 8)
    if (q.course) p.set('course', '1');
    return request<SuggestionsResponse>(`/api/suggestions?${p.toString()}`);
  },

  /* ------------------------------------------------- admin (Phase 6, Lane A) */

  adminOverview: () => request<AdminOverview>('/api/admin/overview'),
  adminUsers: () => request<AdminUser[]>('/api/admin/users'),
  /** role override (null clears it) and/or deactivate; 409 for the last admin */
  updateAdminUser: (id: number, changes: AdminUserPatch) => patch<AdminUser>(`/api/admin/users/${id}`, changes),
  transferOwnership: (id: number, body: TransferRequest) => post<TransferResult>(`/api/admin/users/${id}/transfer`, body),
  /** 409 naming what to transfer while the account owns anything */
  deleteAdminUser: (id: number) => request<void>(`/api/admin/users/${id}`, { method: 'DELETE' }),
  adminTokens: () => request<AdminToken[]>('/api/admin/tokens'),
  adminRevokeToken: (id: number) => request<void>(`/api/admin/tokens/${id}`, { method: 'DELETE' }),
  adminJobs: () => request<JobStatus[]>('/api/admin/jobs'),
  runAdminJob: (name: string) =>
    post<{ ok: boolean; message: string; jobs: JobStatus[] }>(`/api/admin/jobs/${encodeURIComponent(name)}/run`),
  backups: () => request<BackupList>('/api/admin/backups'),
  backupNow: () => post<BackupResult>('/api/admin/backups'),
  backupUrl: (name: string) => `/api/admin/backups/${encodeURIComponent(name)}`,
  cacheStats: () => request<CacheStats>('/api/admin/cache'),
  clearCache: (days: number) => post<EvictResult>('/api/admin/cache/clear', { days }),

  /* --------------------------------------------------------------- tokens */

  tokens: () => request<ApiTokenView[]>('/api/tokens'),
  createApiToken: (name: string, scopes: string[]) =>
    post<ApiTokenView>('/api/tokens', { name, scopes }),
  revokeApiToken: (id: number) => request<void>(`/api/tokens/${id}`, { method: 'DELETE' }),
};

export const mediaUrl = {
  render: (rel: string | null) => (rel ? `/media/render/${rel}` : null),
  asset: (rel: string | null) => (rel ? `/media/assets/${rel}` : null),
  thumb: (rel: string | null) => (rel ? `/media/thumbs/${rel}` : null),
  cache: (rel: string | null) => (rel ? `/api/render/pdf/${rel}` : null),
};

/* ------------------------------ preferences and saved views (Phase 7, Lane O) */

export const prefsApi = {
  /** this account's stored preferences; `updatedAt` null when nothing is stored yet */
  get: () => request<import('@studio/shared').PrefsResponse>('/api/prefs'),
  /** 409 (ApiError.body = the stored state) when the stored copy is newer than `ifUnmodifiedSince` */
  put: (prefs: Partial<import('@studio/shared').Prefs>, ifUnmodifiedSince?: string | null) =>
    put<import('@studio/shared').PrefsResponse>('/api/prefs', { prefs, replace: true, ifUnmodifiedSince: ifUnmodifiedSince ?? null }),
  views: (scope?: import('@studio/shared').SavedViewScope) =>
    request<import('@studio/shared').SavedView[]>(`/api/views${scope ? `?scope=${scope}` : ''}`),
  createView: (view: { scope: import('@studio/shared').SavedViewScope; name: string; query: string; shared?: boolean }) =>
    post<import('@studio/shared').SavedView>('/api/views', view),
  updateView: (id: number, changes: { name?: string; query?: string; shared?: boolean }) =>
    patch<import('@studio/shared').SavedView>(`/api/views/${id}`, changes),
  deleteView: (id: number) => request<void>(`/api/views/${id}`, { method: 'DELETE' }),
};

/* ------------------------- print layouts and accessible PDFs (Phase 7, Lane P) */

export type PrintLayout = '2up' | 'booklet';

export interface PrintLayoutResult {
  ok: boolean;
  /** relative to the render cache, for `mediaUrl.cache` */
  pdf: string | null;
  /** printed sides */
  pages: number;
  sourcePages: number;
  sheets?: number;
  sheetsDuplex?: number;
  layout: PrintLayout;
  variant: RenderVariant;
  cached?: boolean;
  errors?: { message: string }[];
  error?: string;
}

export interface AccessibleProblem {
  itemId: string | null;
  message: string;
  hint: string;
  file: string | null;
  line: number | null;
}

export interface AccessibleResult extends RenderResult {
  available: boolean;
  reason?: string;
  problems: AccessibleProblem[];
  standard: 'ua-1';
}

export const printApi = {
  /** the sheet's PDF in `layout`; the sheet is built (or taken from the cache) first */
  layout: (id: string, opts: { layout: PrintLayout; variant: RenderVariant; format: 'latex' | 'typst'; force?: boolean }) =>
    post<PrintLayoutResult>(`/api/render/sheet/${id}/print`, opts),
  /** whether the sheet has an accessible build at all (Typst sheets only) */
  accessibleAvailable: (id: string) =>
    request<{ available: boolean; standard: 'ua-1'; reason?: string }>(`/api/render/sheet/${id}/accessible`),
  /** the sheet as PDF/UA-1, or the reasons typst refused, on their items */
  accessible: (id: string, opts: { variant: RenderVariant; force?: boolean }) =>
    post<AccessibleResult>(`/api/render/sheet/${id}/accessible`, opts),
};
